// @ts-nocheck
import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useTheme } from '../contexts/ThemeContext';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Target, Shield, Lightbulb, TrendingUp, DollarSign, Sparkles, Zap, Brain } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8001';
const WS_BASE = API_BASE.replace(/^http/, 'ws');

const StrategyAdvisor = () => {
  const { isDark } = useTheme();

  // Load persisted state from localStorage on mount
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [analysis, setAnalysis] = useState(() => {
    const saved = localStorage.getItem('strategy_advisor_analysis');
    return saved || '';
  });
  const [sections, setSections] = useState(() => {
    const saved = localStorage.getItem('strategy_advisor_sections');
    return saved ? JSON.parse(saved) : {};
  });
  const [selectedSection, setSelectedSection] = useState(() => {
    const saved = localStorage.getItem('strategy_advisor_selected_section');
    return saved || null;
  });
  const [error, setError] = useState(null);

  // Persist state to localStorage whenever it changes
  useEffect(() => {
    if (analysis) {
      localStorage.setItem('strategy_advisor_analysis', analysis);
    }
  }, [analysis]);

  useEffect(() => {
    if (Object.keys(sections).length > 0) {
      localStorage.setItem('strategy_advisor_sections', JSON.stringify(sections));
    }
  }, [sections]);

  useEffect(() => {
    if (selectedSection) {
      localStorage.setItem('strategy_advisor_selected_section', selectedSection);
    }
  }, [selectedSection]);

  const reportSections = [
    {
      id: 'executive_summary',
      title: 'Executive Summary',
      icon: Target,
      emoji: '🎯',
      color: 'blue',
      description: 'High-level market outlook'
    },
    {
      id: 'cross_strategy',
      title: 'Cross-Strategy Analysis',
      icon: TrendingUp,
      emoji: '📊',
      color: 'purple',
      description: 'Multi-strategy consensus'
    },
    {
      id: 'risk_assessment',
      title: 'Risk Assessment',
      icon: Shield,
      emoji: '🛡️',
      color: 'orange',
      description: 'Defensive & aggressive plays'
    },
    {
      id: 'key_insights',
      title: 'Key Insights',
      icon: Lightbulb,
      emoji: '💡',
      color: 'yellow',
      description: 'Market signals & trends'
    },
    {
      id: 'recommendations',
      title: 'Actionable Recommendations',
      icon: DollarSign,
      emoji: '🎲',
      color: 'green',
      description: 'Specific BUY/HOLD/AVOID'
    }
  ];

  const parseAnalysisIntoSections = (fullAnalysis) => {
    if (!fullAnalysis || fullAnalysis.trim().length === 0) {
      return {};
    }

    const parsedSections = {};

    // Split by ## headers (h2 in markdown), but only at start of line
    // This regex ensures we only split on h2 headers (##), not h3 (###)
    const parts = fullAnalysis.split(/^##\s+/m);

    parts.forEach((part) => {
      const trimmedPart = part.trim();
      if (trimmedPart.length === 0) return;

      const lines = trimmedPart.split('\n');
      const header = lines[0].trim();
      const content = lines.slice(1).join('\n').trim();

      // Skip if no header
      if (!header || header.length === 0) return;

      // Remove emojis and extra characters for matching
      const cleanHeader = header.replace(/[^\w\s-]/g, '').trim();
      const lowerHeader = header.toLowerCase();
      const lowerCleanHeader = cleanHeader.toLowerCase();

      // Map headers to section IDs (case-insensitive, emoji-tolerant)
      if (lowerHeader.includes('executive summary') || lowerCleanHeader.includes('executive summary')) {
        parsedSections.executive_summary = content;
      } else if (lowerHeader.includes('cross-strategy') || lowerHeader.includes('cross strategy') || lowerCleanHeader.includes('crossstrategy') || lowerCleanHeader.includes('cross strategy')) {
        parsedSections.cross_strategy = content;
      } else if (lowerHeader.includes('risk assessment') || lowerCleanHeader.includes('risk assessment')) {
        parsedSections.risk_assessment = content;
      } else if (lowerHeader.includes('key insights') || lowerCleanHeader.includes('key insights')) {
        parsedSections.key_insights = content;
      } else if (lowerHeader.includes('actionable recommendations') || lowerHeader.includes('recommendations') || lowerCleanHeader.includes('actionable recommendations') || lowerCleanHeader.includes('recommendations')) {
        parsedSections.recommendations = content;
      }
    });

    console.log('📋 Parsed sections:', Object.keys(parsedSections));
    return parsedSections;
  };

  const startAnalysis = () => {
    setLoading(true);
    setProgress(0);
    setStatusMessage('Connecting...');
    setAnalysis('');
    setSections({});
    setSelectedSection(null);
    setError(null);

    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) {
      setError('Please log in to use the AI Strategy Advisor');
      setLoading(false);
      return;
    }

    const ws = new WebSocket(`${WS_BASE}/api/strategy-advisor/ws/analyze?token=${token}`);

    ws.onopen = () => {
      console.log('✅ Strategy Advisor WebSocket connected');
    };

    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      console.log('📩 Strategy Advisor message:', data);

      switch (data.type) {
        case 'status':
          setStatusMessage(data.message);
          setProgress(data.progress || 0);
          break;

        case 'progress':
          setStatusMessage(data.message);
          setProgress(data.progress || 0);
          break;

        case 'stream':
          // Append streaming content (but don't parse sections yet - wait for complete)
          setAnalysis(prev => prev + data.content);
          setProgress(data.progress || 50);
          setStatusMessage('Generating analysis...');
          break;

        case 'complete':
          const fullAnalysis = data.analysis;
          console.log('✅ Analysis complete, full length:', fullAnalysis?.length);
          console.log('📄 Full analysis text:', fullAnalysis);
          setAnalysis(fullAnalysis);
          const parsed = parseAnalysisIntoSections(fullAnalysis);
          console.log('📊 Parsed sections count:', Object.keys(parsed).length);
          console.log('📋 Section details:', parsed);
          setSections(parsed);
          setProgress(100);
          setStatusMessage('Analysis complete!');
          setLoading(false);
          // Auto-select first section that has content
          if (Object.keys(parsed).length > 0) {
            const firstSectionWithContent = reportSections.find(s => parsed[s.id] && parsed[s.id].trim().length > 0);
            if (firstSectionWithContent) {
              setSelectedSection(firstSectionWithContent.id);
              console.log('🎯 Auto-selected section:', firstSectionWithContent.id);
            }
          }
          break;

        case 'error':
        case 'limit_exceeded':
          setError(data.message);
          setLoading(false);
          break;

        default:
          console.warn('Unknown message type:', data.type);
      }
    };

    ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      setError('Connection error. Please try again.');
      setLoading(false);
    };

    ws.onclose = () => {
      console.log('🔌 Strategy Advisor WebSocket closed');
      if (loading) {
        setLoading(false);
      }
    };
  };

  const getColorClasses = (color) => {
    const colors = {
      blue: {
        gradient: 'from-blue-500 to-blue-600',
        bg: isDark ? 'bg-blue-500/10' : 'bg-blue-50',
        border: isDark ? 'border-blue-500/30' : 'border-blue-200',
        text: isDark ? 'text-blue-400' : 'text-blue-600'
      },
      purple: {
        gradient: 'from-purple-500 to-purple-600',
        bg: isDark ? 'bg-purple-500/10' : 'bg-purple-50',
        border: isDark ? 'border-purple-500/30' : 'border-purple-200',
        text: isDark ? 'text-purple-400' : 'text-purple-600'
      },
      orange: {
        gradient: 'from-orange-500 to-orange-600',
        bg: isDark ? 'bg-orange-500/10' : 'bg-orange-50',
        border: isDark ? 'border-orange-500/30' : 'border-orange-200',
        text: isDark ? 'text-orange-400' : 'text-orange-600'
      },
      yellow: {
        gradient: 'from-yellow-500 to-yellow-600',
        bg: isDark ? 'bg-yellow-500/10' : 'bg-yellow-50',
        border: isDark ? 'border-yellow-500/30' : 'border-yellow-200',
        text: isDark ? 'text-yellow-400' : 'text-yellow-600'
      },
      green: {
        gradient: 'from-green-500 to-green-600',
        bg: isDark ? 'bg-green-500/10' : 'bg-green-50',
        border: isDark ? 'border-green-500/30' : 'border-green-200',
        text: isDark ? 'text-green-400' : 'text-green-600'
      }
    };
    return colors[color] || colors.blue;
  };

  const renderSectionCards = () => {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {reportSections.map(section => {
          const Icon = section.icon;
          const colors = getColorClasses(section.color);
          const hasContent = sections[section.id] && sections[section.id].trim().length > 0;
          const isSelected = selectedSection === section.id;
          const isLoading = loading && !hasContent;

          return (
            <div
              key={section.id}
              onClick={() => hasContent && setSelectedSection(section.id)}
              className={`group relative rounded-2xl ${
                isDark ? 'bg-zinc-800/50' : 'bg-white'
              } border-2 ${
                isSelected
                  ? `${colors.border} shadow-xl`
                  : isDark ? 'border-white/10' : 'border-gray-200'
              } shadow-lg hover:shadow-2xl transition-all duration-300 ${
                hasContent ? 'cursor-pointer transform hover:scale-[1.03] hover:-translate-y-1' : ''
              } ${isLoading ? 'animate-pulse' : ''}`}
            >
              {/* Gradient overlay */}
              <div className="absolute inset-0 overflow-hidden rounded-2xl">
                <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradient} ${
                  hasContent ? 'opacity-0 group-hover:opacity-[0.15]' : isLoading ? 'opacity-[0.08] animate-pulse' : 'opacity-[0.03]'
                } transition-opacity duration-300`}></div>
              </div>

              <div className="relative p-6">
                <div className="flex items-start gap-4">
                  {/* Icon */}
                  <div className={`p-3 rounded-xl bg-gradient-to-br ${colors.gradient} text-white shadow-lg flex-shrink-0 ${
                    isLoading ? 'animate-pulse' : ''
                  }`}>
                    <Icon className="w-6 h-6" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className={`text-base font-bold mb-1 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {section.emoji} {section.title}
                    </h3>
                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      {section.description}
                    </p>

                    {/* Status indicator */}
                    <div className="mt-3">
                      {hasContent ? (
                        <span className={`text-xs font-medium ${colors.text}`}>
                          ✓ Ready to view
                        </span>
                      ) : isLoading ? (
                        <div className="flex items-center gap-1">
                          <div className={`w-1 h-1 rounded-full ${colors.bg} animate-bounce`} style={{ animationDelay: '0ms' }}></div>
                          <div className={`w-1 h-1 rounded-full ${colors.bg} animate-bounce`} style={{ animationDelay: '150ms' }}></div>
                          <div className={`w-1 h-1 rounded-full ${colors.bg} animate-bounce`} style={{ animationDelay: '300ms' }}></div>
                          <span className={`text-xs ml-2 ${colors.text}`}>Generating...</span>
                        </div>
                      ) : (
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Waiting...
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderSelectedSection = () => {
    if (!selectedSection || !sections[selectedSection]) return null;

    const section = reportSections.find(s => s.id === selectedSection);
    const colors = getColorClasses(section.color);

    return (
      <div className={`mt-8 rounded-2xl shadow-xl border-2 ${colors.border} ${
        isDark ? 'bg-zinc-800/50' : 'bg-white'
      } overflow-hidden`}>
        {/* Header */}
        <div className={`px-8 py-6 bg-gradient-to-r ${colors.gradient}`}>
          <h2 className="text-2xl font-bold text-white flex items-center gap-3">
            {section.emoji} {section.title}
          </h2>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className={`prose prose-lg max-w-none ${
            isDark
              ? 'prose-invert prose-headings:text-white prose-p:text-gray-300 prose-strong:text-white prose-li:text-gray-300 prose-a:text-blue-400'
              : 'prose-headings:text-gray-900 prose-p:text-gray-700 prose-strong:text-gray-900 prose-li:text-gray-700 prose-a:text-blue-600'
          }`}>
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {sections[selectedSection]}
            </ReactMarkdown>
          </div>
        </div>
      </div>
    );
  };

  const renderSidebar = () => {
    const completedCount = reportSections.filter(s => sections[s.id] && sections[s.id].trim().length > 0).length;

    return (
      <div className={`rounded-2xl overflow-hidden ${
        isDark ? 'bg-zinc-800/50' : 'bg-white'
      } border ${isDark ? 'border-white/10' : 'border-gray-200'} shadow-lg`}>
        {/* Header */}
        <div className={`p-4 border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
          <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Analysis Progress
          </h3>
          <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {completedCount}/{reportSections.length} sections complete
          </p>
        </div>

        {/* Section List */}
        <div className="p-2">
          {reportSections.map((section, index) => {
            const Icon = section.icon;
            const colors = getColorClasses(section.color);
            const hasContent = sections[section.id] && sections[section.id].trim().length > 0;
            const isSelected = selectedSection === section.id;
            const isLoading = loading && !hasContent;

            return (
              <button
                key={section.id}
                onClick={() => hasContent && setSelectedSection(section.id)}
                className={`w-full p-3 rounded-lg mb-2 text-left transition-all ${
                  isSelected
                    ? `${colors.bg} ${colors.border} border-2`
                    : isDark
                    ? 'hover:bg-white/5 border-2 border-transparent'
                    : 'hover:bg-gray-50 border-2 border-transparent'
                } ${hasContent ? 'cursor-pointer' : 'cursor-default'} ${
                  isLoading ? 'animate-pulse' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  {/* Icon */}
                  <div className={`p-2 rounded-lg bg-gradient-to-br ${colors.gradient} text-white flex-shrink-0`}>
                    <Icon className="w-4 h-4" />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      {section.emoji} {section.title}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {hasContent ? (
                        <span className={`text-xs font-medium ${colors.text}`}>
                          ✓ Ready
                        </span>
                      ) : isLoading ? (
                        <div className="flex items-center gap-1">
                          <div className={`w-1 h-1 rounded-full bg-blue-500 animate-bounce`} style={{ animationDelay: '0ms' }}></div>
                          <div className={`w-1 h-1 rounded-full bg-blue-500 animate-bounce`} style={{ animationDelay: '150ms' }}></div>
                          <div className={`w-1 h-1 rounded-full bg-blue-500 animate-bounce`} style={{ animationDelay: '300ms' }}></div>
                          <span className={`text-xs ml-1 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                            Loading...
                          </span>
                        </div>
                      ) : (
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                          Waiting
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Number Badge */}
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                    hasContent
                      ? `${colors.bg} ${colors.text}`
                      : isDark ? 'bg-gray-700 text-gray-500' : 'bg-gray-100 text-gray-400'
                  }`}>
                    {index + 1}
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Footer */}
        {loading && (
          <div className={`p-4 border-t ${isDark ? 'border-white/10 bg-white/5' : 'border-gray-200 bg-gray-50'}`}>
            <div className="flex items-center justify-between text-sm mb-2">
              <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Progress</span>
              <span className={`font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                {progress}%
              </span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-200'}`}>
              <div
                className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-300"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className={`min-h-screen ${isDark ? 'bg-zinc-900' : 'bg-gray-50'}`}>
        {/* Hero Section */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-purple-600 via-blue-600 to-indigo-600 opacity-10"></div>
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iIzAwMCIgb3BhY2l0eT0iLjA1Ii8+PC9nPjwvc3ZnPg==')] opacity-30"></div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
            <div className="text-center">
              <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-3 sm:mb-4">
                <span className="bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-600 bg-clip-text text-transparent">
                  AI Strategy
                </span>
                <br />
                <span className={isDark ? 'text-white' : 'text-gray-900'}>Advisor</span>
              </h1>
              <p className={`text-base sm:text-lg mb-3 sm:mb-2 max-w-2xl mx-auto ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                Get comprehensive AI-powered recommendations by analyzing all 6 trading strategies together
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
          {/* Start Analysis Button */}
          {!loading && Object.keys(sections).length === 0 && (
            <div className={`rounded-2xl shadow-lg border-2 p-8 text-center ${
              isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200'
            }`}>
              <p className={`text-lg mb-6 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                Click below to get AI-powered trading recommendations based on real-time analysis of all trading strategies
              </p>
              <button
                onClick={startAnalysis}
                className={`px-8 py-4 rounded-lg font-semibold shadow-lg transition-all duration-200 text-lg ${
                  isDark
                    ? 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-500 hover:to-blue-500 hover:shadow-xl'
                    : 'bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:from-purple-700 hover:to-blue-700 hover:shadow-xl'
                }`}
              >
                🚀 Start AI Analysis
              </button>
            </div>
          )}

          {/* Loading State */}
          {loading && (
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Main Content Area */}
              <div className="flex-1">
                {/* Progress Header */}
                <div className={`rounded-2xl shadow-lg border p-6 mb-6 ${
                  isDark ? 'bg-white/[0.03] border-white/10' : 'bg-white border-gray-200'
                }`}>
                  <div className="flex items-center gap-4 mb-4">
                    <div className={`animate-spin rounded-full h-12 w-12 border-b-4 ${
                      isDark ? 'border-purple-400' : 'border-purple-600'
                    }`}></div>
                    <div className="flex-1">
                      <p className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                        {statusMessage}
                      </p>
                      <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                        Analyzing {reportSections.length} strategy dimensions...
                      </p>
                    </div>
                    <div className={`text-2xl font-bold ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                      {progress}%
                    </div>
                  </div>

                  {/* Progress Bar */}
                  <div className={`w-full h-2 rounded-full overflow-hidden ${
                    isDark ? 'bg-gray-800' : 'bg-gray-200'
                  }`}>
                    <div
                      className="h-full bg-gradient-to-r from-purple-600 to-blue-600 transition-all duration-300 ease-out"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>

                {/* Section cards - mobile/tablet view */}
                <div className="lg:hidden">
                  <h3 className={`text-lg font-semibold mb-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                    📊 Analysis Sections
                  </h3>
                  {renderSectionCards()}
                </div>

                {/* Show selected section content as it streams in */}
                {selectedSection && sections[selectedSection] && (
                  <div className="mt-6">
                    {renderSelectedSection()}
                  </div>
                )}
              </div>

              {/* Sticky Sidebar - desktop only */}
              <div className="hidden lg:block lg:w-80 flex-shrink-0">
                <div className="sticky top-6">
                  {renderSidebar()}
                </div>
              </div>
            </div>
          )}

          {/* Analysis Results */}
          {!loading && Object.keys(sections).length > 0 && (
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Main Content Area */}
              <div className="flex-1">
                {/* Header with refresh button */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                      📊 AI Strategy Analysis
                    </h2>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                      Click on any section to view detailed insights
                    </p>
                  </div>
                  <button
                    onClick={startAnalysis}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                      isDark
                        ? 'bg-purple-600 text-white hover:bg-purple-500'
                        : 'bg-purple-600 text-white hover:bg-purple-700'
                    }`}
                  >
                    🔄 Refresh Analysis
                  </button>
                </div>

                {/* Section Cards - mobile/tablet view */}
                <div className="lg:hidden mb-6">
                  {renderSectionCards()}
                </div>

                {/* Selected Section Content */}
                {renderSelectedSection()}

                {/* Action Buttons */}
                <div className="mt-6 flex flex-col sm:flex-row gap-4">
                  <button
                    onClick={() => window.location.href = '/explore'}
                    className={`flex-1 px-6 py-3 rounded-lg font-medium border transition-colors ${
                      isDark
                        ? 'border-white/20 text-white hover:bg-white/5'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    📈 View Strategy Tables
                  </button>
                  <button
                    onClick={() => window.location.href = '/agent-analysis'}
                    className={`flex-1 px-6 py-3 rounded-lg font-medium border transition-colors ${
                      isDark
                        ? 'border-white/20 text-white hover:bg-white/5'
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    🔍 Analyze Specific Coin
                  </button>
                </div>
              </div>

              {/* Sticky Sidebar - desktop only */}
              <div className="hidden lg:block lg:w-80 flex-shrink-0">
                <div className="sticky top-6">
                  {renderSidebar()}
                </div>
              </div>
            </div>
          )}

          {/* Error State */}
          {error && (
            <div className={`rounded-2xl shadow-lg border-2 p-8 ${
              isDark
                ? 'bg-red-900/20 border-red-500/30'
                : 'bg-red-50 border-red-200'
            }`}>
              <div className="flex items-center space-x-3 mb-4">
                <span className="text-3xl">⚠️</span>
                <h3 className={`text-xl font-semibold ${
                  isDark ? 'text-red-300' : 'text-red-700'
                }`}>
                  Error
                </h3>
              </div>
              <p className={isDark ? 'text-red-200' : 'text-red-600'}>
                {error}
              </p>
              <button
                onClick={startAnalysis}
                className={`mt-4 px-6 py-2 rounded-lg font-medium transition-colors ${
                  isDark
                    ? 'bg-red-600 text-white hover:bg-red-500'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default StrategyAdvisor;
